import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { AlertService } from '../../../../services/alert.service';
import { ChannelsService } from '../../../../services/channels.service';
import Tooltip from '../../../../components/forms/tooltip.component';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import Switch from '../../../../components/forms/switch.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../components/forms/icon.component';

interface OperatorField {
  rcs_output: string;
  priority: string;
}

interface ChannelModalProps {
  readonly show: boolean;
  readonly onRequestClose: (refresh: boolean) => void;
  readonly payload: any;
  readonly masks: any;
  readonly provider: any;
}

interface MasksField {
  id: number;
  rcs_output: string;
  priority: string;
}

interface FormValues {
  _id: string;
  name: string;
  is_international: boolean;
  [key: string]: any;
}

function MasksModal({ payload, masks, show, onRequestClose }: ChannelModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const [operatorFieldsTelcel, setOperatorFieldsTelcel] = useState<MasksField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsMovistar, setOperatorFieldsMovistar] = useState<MasksField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsAtt, setOperatorFieldsAtt] = useState<MasksField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsOther, setOperatorFieldsOther] = useState<MasksField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const sms_type = [
    { value: 'CORTO', label: 'Número corto' },
    { value: 'LARGO', label: 'Número largo' },
    { value: 'OTP', label: 'OTP' }
  ];
  const [nextIdTelcel, setNextIdTelcel] = useState(1);
  const [nextIdMovistar, setNextIdMovistar] = useState(1);
  const [nextIdAtt, setNextIdAtt] = useState(1);
  const [nextIdOther, setNextIdOther] = useState(1);
  const [atLeastOneFilled, setAtLeastOneFilled] = useState(false);

  const defaultValues: FormValues = {
    _id: '',
    name: '',
    type: '',
    is_international: false
  };

  const {
    register,
    watch,
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const international = watch('is_international');

  const checkIfAtLeastOneFilled = () => {
    if (international) {
      const isFilled = operatorFieldsOther.some(
        (field) => field.rcs_output !== '' && field.priority !== ''
      );
      setAtLeastOneFilled(isFilled);
    } else {
      const operators: OperatorField[][] = [
        operatorFieldsTelcel,
        operatorFieldsMovistar,
        operatorFieldsAtt,
        operatorFieldsOther
      ];
      const isAtLeastOneFilled = operators.some((fields) =>
        fields.some((field) => field.rcs_output !== '' && field.priority !== '')
      );
      setAtLeastOneFilled(isAtLeastOneFilled);
    }
  };

  const allFilledPrioritiesSum100 = () => {
    const validateSum = (fields: OperatorField[]) => {
      const filled = fields.filter((f) => f.rcs_output !== '' && f.priority !== '');
      if (filled.length === 0) return true;
      const sum = filled.reduce((acc, cur) => acc + parseInt(cur.priority || '0', 10), 0);
      return sum === 100;
    };

    if (international) {
      return validateSum(operatorFieldsOther);
    }

    return (
      validateSum(operatorFieldsTelcel) &&
      validateSum(operatorFieldsMovistar) &&
      validateSum(operatorFieldsAtt) &&
      validateSum(operatorFieldsOther)
    );
  };

  const areRelevantFieldsFilled = () => {
    const operators: { fields: OperatorField[]; relevant: boolean }[] = [
      { fields: operatorFieldsTelcel, relevant: false },
      { fields: operatorFieldsMovistar, relevant: false },
      { fields: operatorFieldsAtt, relevant: false },
      { fields: operatorFieldsOther, relevant: true }
    ];

    const allFieldsFilled = (fields: OperatorField[]) =>
      fields.every((field) => field.rcs_output !== '' && field.priority !== '');
    const isRelevant = (fields: OperatorField[]) =>
      fields.some((field) => field.rcs_output !== '' || field.priority !== '');

    if (international) {
      return !isRelevant(operatorFieldsOther) || allFieldsFilled(operatorFieldsOther);
    } else {
      return operators.every(({ fields }) => !isRelevant(fields) || allFieldsFilled(fields));
    }
  };

  const handleAgentsChange = (operatorType: string, id: number, value: string) => {
    const operators: Record<
      string,
      { fields: MasksField[]; setFields: React.Dispatch<React.SetStateAction<MasksField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };

    const { fields: agentFields, setFields } = operators[operatorType];
    setFields(
      agentFields.map((field) => {
        if (field.id === id) {
          return {
            ...field,
            rcs_output: value,
            priority:
              agentFields.length === 1 ||
              agentFields.every((f) => f.rcs_output === '' && f.priority === '')
                ? '100'
                : field.priority
          };
        }
        return field;
      })
    );
  };

  const handlePriorityChange = (operatorType: string, id: number, value: string) => {
    const operators: Record<
      string,
      { fields: MasksField[]; setFields: React.Dispatch<React.SetStateAction<MasksField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };

    const operator = operators[operatorType];
    if (!operator) {
      console.error(`Operator type ${operatorType} not found.`);
      return;
    }

    const { fields: agentFields, setFields } = operator;
    setFields(
      agentFields.map((field) => (field.id === id ? { ...field, priority: value } : field))
    );
  };

  const addMasksField = (operatorType: string, agentOptionsCount: number) => {
    const operators: Record<
      string,
      {
        fields: MasksField[];
        setFields: React.Dispatch<React.SetStateAction<MasksField[]>>;
        nextId: number;
        setNextId: React.Dispatch<React.SetStateAction<number>>;
      }
    > = {
      telcel: {
        fields: operatorFieldsTelcel,
        setFields: setOperatorFieldsTelcel,
        nextId: nextIdTelcel,
        setNextId: setNextIdTelcel
      },
      movistar: {
        fields: operatorFieldsMovistar,
        setFields: setOperatorFieldsMovistar,
        nextId: nextIdMovistar,
        setNextId: setNextIdMovistar
      },
      att: {
        fields: operatorFieldsAtt,
        setFields: setOperatorFieldsAtt,
        nextId: nextIdAtt,
        setNextId: setNextIdAtt
      },
      other: {
        fields: operatorFieldsOther,
        setFields: setOperatorFieldsOther,
        nextId: nextIdOther,
        setNextId: setNextIdOther
      }
    };

    const { fields: agentFields, setFields, nextId, setNextId } = operators[operatorType];
    const maxRows = Math.min(agentOptionsCount, 10);
    const firstRowFilled =
      agentFields?.length > 0 && agentFields[0].rcs_output !== '' && agentFields[0].priority !== '';
    const canAddMoreRows = agentFields?.length < maxRows;

    if (firstRowFilled && canAddMoreRows) {
      const newFields = [...agentFields, { id: nextId, rcs_output: '', priority: '' }];
      const evenPriority = Math.floor(100 / newFields.length);
      const remainder = 100 % newFields.length;
      const updatedFields = newFields.map((field, index) => ({
        ...field,
        priority: `${evenPriority + (index === 0 ? remainder : 0)}`
      }));
      setFields(updatedFields);
      setNextId(nextId + 1);
    }
  };

  const removeMasksField = (operatorType: string, id: number) => {
    const operators: Record<
      string,
      { fields: MasksField[]; setFields: React.Dispatch<React.SetStateAction<MasksField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };
    const { fields: agentFields, setFields } = operators[operatorType];
    if (agentFields?.length > 1) {
      setFields(agentFields.filter((field) => field.id !== id));
    }
    checkIfAtLeastOneFilled();
  };

  const renderMasksFields = (operatorType: string, agentFields: MasksField[]) => {
    const firstRowFilled =
      agentFields?.length > 0 && agentFields[0].rcs_output !== '' && agentFields[0].priority !== '';
    const agentOptionsCount = masks[operatorType]?.length;
    let maxRows = 10;
    if (agentOptionsCount <= 10) {
      maxRows = agentOptionsCount;
    }

    return (
      <>
        {agentFields.map((field) => {
          const selectedAgents = agentFields
            .map((f) => f.rcs_output)
            ?.filter((agent) => agent !== field.rcs_output);
          const agentOptions = international
            ? masks['other']
                ?.filter((agent: any) => agent.is_international)
                .filter((agent: any) => !selectedAgents.includes(agent._id))
                .map((agent: any) => ({
                  value: agent._id,
                  label: agent.name
                }))
            : masks[operatorType]
                ?.filter((agent: any) => !agent.is_international)
                .filter((agent: any) => !selectedAgents.includes(agent._id))
                .map((agent: any) => ({
                  value: agent._id,
                  label: agent.name
                }));
          const priorityOptions = Array.from({ length: 100 }, (_, i) => ({
            value: `${i + 1}`,
            label: `${i + 1}`
          }));

          return (
            <div className="form-group row" key={field.id}>
              <div className="col-md-7">
                <label className="form-label">{t('channels.rcs.outputs')}</label>
                <Select
                  value={agentOptions?.find((agent: any) => agent.value === field.rcs_output)}
                  onChange={(selectedOption) =>
                    handleAgentsChange(
                      operatorType,
                      field.id,
                      selectedOption ? selectedOption.value : ''
                    )
                  }
                  options={agentOptions}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                  }
                  placeholder={t('Selecciona una salida')}
                  className="form-select-custom"
                  classNamePrefix="form-select-custom"
                  isClearable
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('channels.rcs.modal-C-seccion2-priority')}</label>
                <Select
                  value={priorityOptions?.find((option) => option.value === field.priority)}
                  onChange={(selectedOption) =>
                    handlePriorityChange(
                      operatorType,
                      field.id,
                      selectedOption ? selectedOption.value : ''
                    )
                  }
                  options={priorityOptions}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                  }
                  className="form-select-custom"
                  classNamePrefix="form-select-custom"
                  placeholder={t('channels.rcs.modal-C-seccion2-priority')}
                  isClearable
                />
              </div>
              <div className="col-md-1 d-flex align-items-center mb-4">
                {agentFields?.length > 1 && (
                  <div className="col-md-1 mt-0">
                    <button
                      type="button"
                      className="btn btn-light-danger mt-3 mt-md-8"
                      onClick={() => removeMasksField(operatorType, field.id)}
                    >
                      <Icon icon="ki-trash" size="fs-2" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <button
          type="button"
          className={`btn btn-light-info ${agentFields?.length >= maxRows || !firstRowFilled ? 'd-none' : ''}`}
          onClick={() => addMasksField(operatorType, agentOptionsCount)}
          disabled={!firstRowFilled}
        >
          <i className="ki-duotone ki-plus fs-3" />
          {t('channels.rcs.modal-C-seccion2-btnAdd')}
        </button>
      </>
    );
  };

  const registerAgent = async (data: any) => {
    if (!atLeastOneFilled) {
      AlertService.toastError(t('channels.rcs.modal-C-errorValid'));
      return;
    }
    const filterAndMapOperators = (operatorFields: any[]) => {
      if (!operatorFields) return [];
      return operatorFields
        .filter((field: OperatorField) => field.rcs_output !== '' && field.priority !== '')
        .map(({ rcs_output, priority }) => ({
          output: rcs_output,
          priority: Number(priority)
        }));
    };
    const filteredTelcel = international ? [] : filterAndMapOperators(operatorFieldsTelcel);
    const filteredMovistar = international ? [] : filterAndMapOperators(operatorFieldsMovistar);
    const filteredAtt = international ? [] : filterAndMapOperators(operatorFieldsAtt);
    const filteredOther = filterAndMapOperators(operatorFieldsOther);
    let formData: any;

    if (international) {
      formData = {
        name: data.name,
        type: data.type,
        telcel: [],
        movistar: [],
        att: [],
        is_international: true,
        other: [],
        international: filteredOther,
        status: payload?.status || true,
        ...(payload != null && { _id: data._id })
      };
    } else {
      formData = {
        name: data.name,
        type: data.type,
        is_international: false,
        telcel: filteredTelcel,
        movistar: filteredMovistar,
        att: filteredAtt,
        other: filteredOther,
        international: [],
        status: payload?.status || true,
        ...(payload != null && { _id: data._id })
      };
    }

    const messageQuestion = payload
      ? '¿Deseas editar este agente?'
      : '¿Deseas registrar este nuevo agente?';

    const isConfirmed = await AlertService.question(
      t('general.atention'),
      messageQuestion + '<br><br>',
      () => Promise.resolve(true)
    );

    if (!isConfirmed) return;
    try {
      if (payload != null) {
        await ChannelsService.updateMasksSMS(formData);
        AlertService.toastSuccess(t('channels.rcs.modal-C-succesEdit'));
      } else {
        await ChannelsService.addMasksSMS(formData);

        AlertService.toastSuccess(t('channels.rcs.modal-C-succes'));
      }
      onRequestClose(true);
      resetForm();
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const resetFields = () => {
    const initialFields = [{ id: 0, rcs_output: '', priority: '' }];
    setOperatorFieldsTelcel(initialFields);
    setOperatorFieldsMovistar(initialFields);
    setOperatorFieldsAtt(initialFields);
    setOperatorFieldsOther(initialFields);
    setNextIdTelcel(1);
    setNextIdMovistar(1);
    setNextIdAtt(1);
    setNextIdOther(1);
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
    setOperatorFieldsTelcel([]);
    setOperatorFieldsMovistar([]);
    setOperatorFieldsAtt([]);
    setOperatorFieldsOther([]);
    setNextIdTelcel(1);
    setNextIdMovistar(1);
    setNextIdAtt(1);
    setNextIdOther(1);
  };

  useEffect(() => {
    if (show) {
      resetFields();
    }
  }, [show]);

  useEffect(() => {
    checkIfAtLeastOneFilled();
  }, [
    operatorFieldsTelcel,
    operatorFieldsMovistar,
    operatorFieldsAtt,
    operatorFieldsOther,
    international
  ]);

  useEffect(() => {
    const updateOperatorFields = (field: string, data: any[]) => {
      if (data?.length > 0) {
        return data.map((item: any, index: number) => ({
          id: index,
          rcs_output: item.output,
          priority: item.priority.toString()
        }));
      } else {
        return [{ id: 0, rcs_output: '', priority: '' }];
      }
    };

    if (payload) {
      const telcelFields = updateOperatorFields('telcel', payload.telcel);
      setOperatorFieldsTelcel(telcelFields);
      setNextIdTelcel(payload.telcel?.length || 1);

      const movistarFields = updateOperatorFields('movistar', payload.movistar);
      setOperatorFieldsMovistar(movistarFields);
      setNextIdMovistar(payload.movistar?.length || 1);

      const attFields = updateOperatorFields('att', payload.att);
      setOperatorFieldsAtt(attFields);
      setNextIdAtt(payload.att?.length || 1);

      const otherFields = payload.is_international
        ? updateOperatorFields('other', payload.international)
        : updateOperatorFields('other', payload.other);
      setOperatorFieldsOther(otherFields);
      setNextIdOther(
        payload.is_international ? payload.international?.length || 1 : payload.other?.length || 1
      );

      reset({
        _id: payload?._id || defaultValues._id,
        name: payload?.name ?? defaultValues.name,
        type: typeof payload?.type === 'string' ? payload.type.toUpperCase() : defaultValues.type,
        is_international: payload?.is_international ?? defaultValues.is_international
      });
    }
  }, [payload]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <button
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
              type="button"
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </button>
          </div>
          <form onSubmit={handleSubmit(registerAgent)}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {watch('_id') ? (
                    <>
                      Edita la máscara{' '}
                      <span className="link-primary fw-bold ms-2">{payload?.name}</span>
                    </>
                  ) : (
                    <>Registra nueva máscara</>
                  )}
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row mt-10">
                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <Title
                    title={t('channels.rcs.modal-C-seccion1-title')}
                    subtitle={t('channels.rcs.modal-C-seccion1-Subtitle2')}
                    icon="ki-pencil"
                    type="info"
                  />
                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.rcs.tc-name')}
                      requiredIndicator="required"
                      placeholder={t('channels.rcs.modal-C-seccion1-placeholder')}
                      errors={errors.name}
                      {...register('name', { required: true })}
                    />
                  </div>
                  <div className="fv-row mb-20">
                    <label className="form-label required">{t('general.type')}</label>
                    <Controller
                      control={control}
                      name="type"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            isDisabled={watch('profile') === ''}
                            options={sms_type}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={'Selecciona tipo de número'}
                            onChange={(selectedOption) => {
                              onChange(selectedOption ? selectedOption.value : null);
                              setValue('type', selectedOption ? selectedOption.value : '');
                            }}
                            value={sms_type?.find((rcs: any) => rcs.value === value) || null}
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                  <div className="row mb-20">
                    <div className="d-flex flex-stack">
                      <div className="d-flex">
                        <span className="d-flex align-items-center">
                          <i className="bi bi-globe-americas fs-3x hover-scale me-2 me-6"></i>
                        </span>
                        <div className="d-flex flex-column">
                          <span className="fs-5 text-dark text-hover-primary fw-bold">
                            {t('channels.rcs.international')}
                          </span>
                          <div className="fs-6 fw-semibold text-gray-400 mw-300px">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t('channels.rcs.modal-C-seccion1-subtitle-inter')
                              }}
                            ></span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-end">
                        <div className="form-check form-check-solid form-check-custom form-switch">
                          <Switch {...register('is_international')} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 rounded h-100 p-3">
                  <Title
                    title={
                      <span className="d-flex align-items-center">
                        {t('channels.rcs.modal-C-seccion2-title')}
                      </span>
                    }
                    subtitle={t('channels.rcs.modal-C-seccion2-Subtitle')}
                    longDescription={
                      'La asignación de prioridades se realiza por operador. Si asignas varias máscaras a un operador, la suma total de las prioridades entre todas las máscaras seleccionadas debe ser igual a 100.'
                    }
                    icon="ki-satellite"
                  />
                  {!international && (
                    <>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img
                          src="/assets/media/operators/telcel.png"
                          alt="imageMask"
                          width="70px"
                        />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/telcel-dark.png"
                          alt="imageMask"
                          width="70px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderMasksFields('telcel', operatorFieldsTelcel)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img
                          src="/assets/media/operators/movistar.png"
                          alt="imageMask"
                          width="80px"
                        />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/movistar-dark.png"
                          alt="imageMask"
                          width="80px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderMasksFields('movistar', operatorFieldsMovistar)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img src="/assets/media/operators/att.png" alt="imageMask" width="70px" />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/att-dark.png"
                          alt="imageMask"
                          width="70px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderMasksFields('att', operatorFieldsAtt)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img src="/assets/media/operators/altan.png" alt="imageMask" width="60px" />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img src="/assets/media/operators/altan.png" alt="imageMask" width="60px" />
                      </div>
                      <div className="fv-row mb-10">
                        {renderMasksFields('other', operatorFieldsOther)}
                      </div>
                    </>
                  )}
                  {international && (
                    <>
                      <div className="fw-bold fs-6 theme-light-show">
                        <img
                          src="/assets/media/operators/altan.png"
                          alt="imageMask"
                          width="60px"
                          height="30px"
                        />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/altan.png"
                          alt="imageMask"
                          width="60px"
                          height="30px"
                        />
                      </div>
                      <div className="fv-row mb-10 mt-5">
                        {renderMasksFields('other', operatorFieldsOther)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={
                    !atLeastOneFilled ||
                    !isValid ||
                    isSubmitting ||
                    !areRelevantFieldsFilled() ||
                    !allFilledPrioritiesSum100()
                  }
                  isLoading={isSubmitting}
                >
                  {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MasksModal;
